import { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import gk2 from '@shamrock-core/gk2';
import { Auth0ContextInterface } from '@auth0/auth0-react';

interface Auth0Context {
  getAccessTokenSilently?: Auth0ContextInterface['getAccessTokenSilently'];
  isAuthenticated?: boolean;
  logout?: Auth0ContextInterface['logout'];
}

const auth0Context: Auth0Context = {
  getAccessTokenSilently: undefined,
  isAuthenticated: false,
  logout: undefined,
};

export const addAuth0Context = (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  isAuthenticated: boolean,
  logout: Auth0ContextInterface['logout'],
) => {
  auth0Context.getAccessTokenSilently = getAccessTokenSilently;
  auth0Context.isAuthenticated = isAuthenticated;
  auth0Context.logout = logout;
};

export const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' },
): BaseQueryFn<
  {
    url: string;
    method: AxiosRequestConfig['method'];
    data?: AxiosRequestConfig['data'];
    params?: AxiosRequestConfig['params'];
  },
  unknown,
  unknown
> => {
  const axiosInstance = axios.create({ baseURL: baseUrl });

  axiosInstance.interceptors.request.use(
    async (config) => {
      let token = LocalAuth.getTokenValue();
      const deviceId = localStorage.getItem('device_id');

      if (auth0Context.getAccessTokenSilently) {
        token = await auth0Context.getAccessTokenSilently();
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;

        if (typeof gk2?.webInterceptor === 'function') {
          config = gk2.webInterceptor(config, false, deviceId);
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return async ({ url, method, data, params }) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      console.error('RTK Query error:', err.response?.status, err.response?.data);

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
};
